import React, { useState, useEffect, useCallback } from "react";
import Layout from "@/components/Layout";
import BudgetRow from "@/components/BudgetRow";
import BudgetFormDialog from "@/components/BudgetFormDialog";
import api, { formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { formatCurrency, monthLabel } from "@/lib/format";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Plus, Loader2, Target, TrendingUp } from "lucide-react";

const months = [
  { value: 1, label: "Enero" }, { value: 2, label: "Febrero" },
  { value: 3, label: "Marzo" }, { value: 4, label: "Abril" },
  { value: 5, label: "Mayo" }, { value: 6, label: "Junio" },
  { value: 7, label: "Julio" }, { value: 8, label: "Agosto" },
  { value: 9, label: "Septiembre" }, { value: 10, label: "Octubre" },
  { value: 11, label: "Noviembre" }, { value: 12, label: "Diciembre" },
];

export default function Budgets() {
  const now = new Date();
  const [month, setMonth] = useState(now.getMonth() + 1);
  const [year, setYear] = useState(now.getFullYear());
  const [usage, setUsage] = useState(null);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [formOpen, setFormOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [confirmDelete, setConfirmDelete] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [u, c] = await Promise.all([
        api.get(`/budgets/usage?month=${month}&year=${year}`),
        api.get(`/categories`),
      ]);
      setUsage(u.data);
      setCategories(c.data);
    } catch (err) {
      if (err?.response?.status !== 401) {
        toast.error(
          formatApiErrorDetail(err?.response?.data?.detail) ||
            "No se pudieron cargar tus presupuestos."
        );
      }
    } finally {
      setLoading(false);
    }
  }, [month, year]);

  useEffect(() => {
    load();
  }, [load]);

  const handleDelete = async () => {
    if (!confirmDelete) return;
    try {
      await api.delete(`/budgets/${confirmDelete.id}`);
      toast.success(`Presupuesto de ${confirmDelete.category} eliminado`);
      setConfirmDelete(null);
      load();
    } catch (err) {
      toast.error(
        formatApiErrorDetail(err.response?.data?.detail) ||
          "No se pudo eliminar"
      );
    }
  };

  const years = [];
  for (let y = now.getFullYear() - 3; y <= now.getFullYear() + 1; y++) years.push(y);

  const currency = usage?.currency || "MXN";
  const items = usage?.budgets || [];
  const totalLimit = usage?.total_limit || 0;
  const totalSpent = usage?.total_spent || 0;
  const totalRemaining = usage?.total_remaining || 0;
  const globalPct = totalLimit > 0 ? Math.min((totalSpent / totalLimit) * 100, 100) : 0;
  const globalColor =
    totalSpent > totalLimit && totalLimit > 0
      ? "#EF4444"
      : totalSpent / Math.max(totalLimit, 1) >= 0.8
      ? "#F59E0B"
      : "#0052FF";

  return (
    <Layout>
      {/* Header */}
      <section className="mb-8 fade-up" data-testid="budgets-header">
        <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-6">
          <div>
            <p className="text-xs uppercase tracking-[0.3em] text-[#94A3B8] mb-3 font-medium">
              Presupuestos · {monthLabel(month, year)}
            </p>
            <h1 className="font-display text-4xl sm:text-5xl font-bold tracking-tight text-[#F1F5F9]">
              Tus límites del mes
            </h1>
            <p className="text-sm text-[#64748B] mt-2">
              Define un techo mensual por categoría y te avisamos cuando te acerques.
            </p>
          </div>

          <div className="flex flex-wrap items-center gap-3">
            <Select value={String(month)} onValueChange={(v) => setMonth(Number(v))}>
              <SelectTrigger
                className="w-[140px] bg-[#131C2E] border-[#2A3447] h-11"
                data-testid="budgets-month-select"
                aria-label="Filtrar por mes"
              >
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="bg-[#131C2E]">
                {months.map((m) => (
                  <SelectItem key={m.value} value={String(m.value)}>
                    {m.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Select value={String(year)} onValueChange={(v) => setYear(Number(v))}>
              <SelectTrigger
                className="w-[100px] bg-[#131C2E] border-[#2A3447] h-11"
                data-testid="budgets-year-select"
                aria-label="Filtrar por año"
              >
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="bg-[#131C2E]">
                {years.map((y) => (
                  <SelectItem key={y} value={String(y)}>
                    {y}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Button
              type="button"
              onClick={() => {
                setEditing(null);
                setFormOpen(true);
              }}
              className="bg-[#0052FF] hover:bg-[#0040CC] text-white rounded-md h-11 px-5 font-medium"
              data-testid="budget-create-button"
            >
              <Plus className="w-4 h-4 mr-2" /> Nuevo presupuesto
            </Button>
          </div>
        </div>
      </section>

      {/* Summary card */}
      <section className="clay-card p-6 mb-8 fade-up" data-testid="budgets-summary">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-[#0052FF]/15 flex items-center justify-center">
              <Target className="w-5 h-5 text-[#0052FF]" />
            </div>
            <div>
              <p className="text-xs uppercase tracking-[0.2em] text-[#94A3B8]">
                Total presupuestado
              </p>
              <p className="font-display text-2xl font-semibold text-[#F1F5F9] font-numeric">
                {formatCurrency(totalLimit, currency)}
              </p>
            </div>
          </div>
          <div className="text-right">
            <p className="text-xs uppercase tracking-[0.2em] text-[#94A3B8]">Gastado</p>
            <p
              className="font-display text-2xl font-semibold font-numeric"
              style={{ color: globalColor }}
              data-testid="budgets-total-spent"
            >
              {formatCurrency(totalSpent, currency)}
            </p>
          </div>
        </div>
        <div className="h-3 bg-[#1B2638] rounded-full overflow-hidden mb-2">
          <div
            className="h-full rounded-full transition-all duration-500"
            style={{ width: `${globalPct}%`, backgroundColor: globalColor }}
          />
        </div>
        <div className="flex items-center justify-between text-xs text-[#64748B] font-numeric">
          <span>
            {totalLimit > 0
              ? `${((totalSpent / totalLimit) * 100).toFixed(1)}% del total`
              : "Crea tu primer presupuesto para empezar"}
          </span>
          <span>
            <TrendingUp className="w-3 h-3 inline mr-1" />
            {formatCurrency(totalRemaining, currency)} disponible
          </span>
        </div>
      </section>

      {/* List */}
      <section data-testid="budgets-list">
        {loading ? (
          <div className="text-center py-16 text-[#94A3B8]">
            <Loader2 className="w-6 h-6 animate-spin mx-auto mb-3" />
            Cargando...
          </div>
        ) : items.length === 0 ? (
          <div className="clay-card-inset p-12 text-center fade-up" data-testid="budgets-empty">
            <div className="w-14 h-14 rounded-2xl bg-[#0052FF]/10 flex items-center justify-center mx-auto mb-4">
              <Target className="w-7 h-7 text-[#0052FF]" />
            </div>
            <h3 className="font-display text-xl font-semibold text-[#F1F5F9] mb-2">
              Aún no tienes presupuestos
            </h3>
            <p className="text-sm text-[#64748B] max-w-md mx-auto mb-5">
              Establece un límite mensual por categoría (Comida, Transporte, etc.) y
              te avisaremos cuando estés cerca de rebasarlo.
            </p>
            <Button
              onClick={() => {
                setEditing(null);
                setFormOpen(true);
              }}
              className="bg-[#0052FF] hover:bg-[#0040CC] text-white"
              data-testid="budgets-empty-create-button"
            >
              <Plus className="w-4 h-4 mr-2" /> Crear mi primer presupuesto
            </Button>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {items.map((it) => (
              <BudgetRow
                key={it.id}
                item={it}
                currency={currency}
                onEdit={(item) => {
                  setEditing(item);
                  setFormOpen(true);
                }}
                onDelete={(item) => setConfirmDelete(item)}
              />
            ))}
          </div>
        )}
      </section>

      <BudgetFormDialog
        open={formOpen}
        onOpenChange={setFormOpen}
        initial={editing}
        categories={categories}
        existing={items}
        onSaved={load}
      />

      <AlertDialog
        open={Boolean(confirmDelete)}
        onOpenChange={(o) => !o && setConfirmDelete(null)}
      >
        <AlertDialogContent className="bg-[#131C2E]" data-testid="budget-delete-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>¿Eliminar este presupuesto?</AlertDialogTitle>
            <AlertDialogDescription>
              Vas a borrar el presupuesto de{" "}
              <strong>{confirmDelete?.category}</strong>. Tus movimientos no se
              eliminan, sólo el límite mensual.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="budget-delete-cancel">
              Cancelar
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDelete}
              className="bg-[#EF4444] hover:bg-[#DC2626] text-white"
              data-testid="budget-delete-confirm"
            >
              Eliminar
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </Layout>
  );
}
